import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { motion } from "motion/react";
import necklaceImage from "@/assets/necklace-1.jpg";
import ringImage from "@/assets/ring-1.jpg";
import braceletImage from "@/assets/bracelet-1.jpg";
import earringsImage from "@/assets/earrings-1.jpg";

interface FeatureItem {
  id: number;
  title: string;
  image: string;
  description: string;
}

const features: FeatureItem[] = [
  {
    id: 1,
    title: "יהלומים ברמה הגבוהה ביותר",
    image: ringImage,
    description:
      "אנו עובדים אך ורק עם יהלומים באיכות הגבוהה ביותר, מאושרים על ידי מעבדות בינלאומיות מובילות. כל יהלום עובר בדיקות קפדניות ומגיע עם אישור איכות מפורט. אנו מתחייבים לשקיפות מלאה בכל שלבי התהליך.",
  },
  {
    id: 2,
    title: "עיצוב וייחודיות",
    image: necklaceImage,
    description:
      "הקולקציות שלנו משלבות עיצוב קלסי עם מגע מודרני ואישי. אנו מציעים גם שירות עיצוב אישי מלא, שבו תוכלו ליצור תכשיט ייחודי שמשקף את האישיות והסגנון המיוחדים שלכם. צוות המעצבים שלנו ילווה אתכם בכל שלב.",
  },
  {
    id: 3,
    title: "מחירים הוגנים ושקופים",
    image: braceletImage,
    description:
      "אנו מאמינים שתכשיטי איכות צריכים להיות נגישים. בזכות עבודה ישירה עם יצרנים מובילים וללא מתווכים, אנו מציעים מחירים הוגנים ותחרותיים ביותר. כל המחירים שלנו שקופים לחלוטין, ללא עלויות נסתרות.",
  },
  {
    id: 4,
    title: "שירות מקצועי ואישי",
    image: earringsImage,
    description:
      "הצוות המנוסה שלנו עומד לרשותכם לייעוץ מקצועי ואישי. אנו מספקים ליווי מלא לאורך כל התהליך - מהבחירה הראשונית ועד לאחר הרכישה. שירות התחזוקה וההתאמות שלנו זמינים לכם תמיד, עם תקופת אחריות מקיפה.",
  },
  {
    id: 5,
    title: "ניסיון של עשרות שנים",
    image: ringImage,
    description:
      "מאז 1984, אליס תכשיטים ושעונים מספקת שירות מצוין ללקוחות ברחבי הדרום. הניסיון הרב שלנו מאפשר לנו להבין את הצרכים שלכם ולהציע פתרונות מותאמים אישית. אנו גאים במוניטין שבנינו לאורך השנים בזכות מקצועיות ואמינות.",
  },
  {
    id: 6,
    title: "אחריות ושירות לאחר מכירה",
    image: necklaceImage,
    description:
      "אנו מציעים אחריות מקיפה על כל התכשיטים שלנו ושירות תיקונים ותחזוקה מקצועי. הצוות שלנו זמין לכל שאלה או בקשה גם לאחר הרכישה, ואנו מתחייבים לשביעות רצונכם המלאה. תכשיט מאליס הוא השקעה לכל החיים.",
  },
];

const WhyChooseAccordion = () => {
  const [activeTabId, setActiveTabId] = useState<number | null>(1);
  const [activeImage, setActiveImage] = useState(features[0].image);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className="py-20 px-4 bg-gradient-to-b from-background via-primary/5 to-background"
    >
      <div className="container mx-auto">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-sm text-primary/60 tracking-[0.3em] uppercase mb-4" style={{ fontWeight: 300 }}>
            למה לבחור בנו
          </p>
          <h2 className="text-3xl md:text-4xl mb-4 tracking-wide" style={{ fontWeight: 300 }}>
            היתרונות שלנו
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-primary/40"></div>
            <div className="w-1.5 h-1.5 bg-primary/60 rotate-45"></div>
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-primary/40"></div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <Accordion type="single" className="w-full" defaultValue="item-1" dir="rtl">
              {features.map((tab) => (
                <AccordionItem key={tab.id} value={`item-${tab.id}`} className="border-b border-border/30">
                  <AccordionTrigger
                    onClick={() => {
                      setActiveImage(tab.image);
                      setActiveTabId(tab.id);
                    }}
                    className="cursor-pointer py-5 hover:no-underline transition-colors"
                  >
                    <h3
                      className={`text-lg md:text-xl text-right transition-colors ${
                        tab.id === activeTabId ? "text-foreground" : "text-muted-foreground"
                      }`}
                      style={{ fontWeight: 400 }}
                    >
                      {tab.title}
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="mt-3 text-muted-foreground text-right leading-relaxed" style={{ fontWeight: 300 }}>
                      {tab.description}
                    </p>
                    <div className="mt-4 md:hidden">
                      <img
                        src={tab.image}
                        alt={tab.title}
                        loading="lazy"
                        className="h-full max-h-80 w-full rounded-md object-cover"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div 
            className="relative hidden md:block overflow-hidden elegant-border bg-secondary/20 rounded-lg sticky top-24"
            initial={{ opacity: 0, x: -50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <img
              src={activeImage}
              alt="תכשיט זהב איכותי מקולקציית אליס - עיצוב יוקרתי בעבודת יד"
              loading="lazy"
              className="aspect-[4/3] rounded-lg object-cover w-full transition-all duration-500"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseAccordion;
